/**
 * Theme Configuration and Management
 * Handles light/dark mode switching and colorblind accessibility mode
 */

import { colors, darkColors, colorblindColors, colorblindDarkColors } from './colors.js'

const THEME_STORAGE_KEY = 'dietedics_theme'
const ACCESSIBILITY_STORAGE_KEY = 'dietedics_accessibility'
const DEFAULT_THEME = 'light'
const DEFAULT_ACCESSIBILITY = false

/**
 * Get current theme from localStorage or default
 */
export function getTheme() {
  if (typeof window === 'undefined') return DEFAULT_THEME
  const stored = localStorage.getItem(THEME_STORAGE_KEY)
  return stored || DEFAULT_THEME
}

/**
 * Set theme and save to localStorage
 */
export function setTheme(theme) {
  if (typeof window === 'undefined') return
  localStorage.setItem(THEME_STORAGE_KEY, theme)
  const accessibilityMode = getAccessibilityMode()
  applyTheme(theme, accessibilityMode)
}

/**
 * Toggle between light and dark theme
 */
export function toggleTheme() {
  const current = getTheme()
  const newTheme = current === 'light' ? 'dark' : 'light'
  setTheme(newTheme)
  return newTheme
}

/**
 * Get accessibility mode from localStorage or default
 */
export function getAccessibilityMode() {
  if (typeof window === 'undefined') return DEFAULT_ACCESSIBILITY
  const stored = localStorage.getItem(ACCESSIBILITY_STORAGE_KEY)
  return stored === 'true'
}

/**
 * Set accessibility mode and save to localStorage
 */
export function setAccessibilityMode(enabled) {
  if (typeof window === 'undefined') return
  localStorage.setItem(ACCESSIBILITY_STORAGE_KEY, enabled.toString())
  applyTheme(getTheme(), enabled)
}

/**
 * Toggle accessibility mode
 */
export function toggleAccessibilityMode() {
  const current = getAccessibilityMode()
  const newMode = !current
  setAccessibilityMode(newMode)
  return newMode
}

/**
 * Apply theme to document root
 */
export function applyTheme(theme, accessibilityMode = null) {
  if (typeof document === 'undefined') return
  
  const root = document.documentElement
  const isDark = theme === 'dark'
  const isColorblind = accessibilityMode !== null ? accessibilityMode : getAccessibilityMode()
  
  // Remove existing theme classes
  root.classList.remove('theme-light', 'theme-dark', 'accessibility-on', 'accessibility-off')
  
  // Add new theme classes
  root.classList.add(`theme-${theme}`)
  root.classList.add(isColorblind ? 'accessibility-on' : 'accessibility-off')
  
  // Apply CSS variables - choose color palette based on theme and accessibility mode
  let themeColors
  if (isColorblind) {
    themeColors = isDark ? colorblindDarkColors : colorblindColors
  } else {
    themeColors = isDark ? darkColors : colors
  }
  
  // Primary colors
  root.style.setProperty('--color-primary-light', themeColors.primary.light)
  root.style.setProperty('--color-primary-main', themeColors.primary.main)
  root.style.setProperty('--color-primary-dark', themeColors.primary.dark)
  root.style.setProperty('--color-primary-accent', themeColors.primary.accent)
  
  // Secondary colors
  root.style.setProperty('--color-secondary-light', themeColors.secondary.light)
  root.style.setProperty('--color-secondary-main', themeColors.secondary.main)
  root.style.setProperty('--color-secondary-dark', themeColors.secondary.dark)
  root.style.setProperty('--color-secondary-accent', themeColors.secondary.accent)
  
  // Background colors
  root.style.setProperty('--color-bg-light', themeColors.background.light)
  root.style.setProperty('--color-bg-main', themeColors.background.main)
  root.style.setProperty('--color-bg-dark', themeColors.background.dark)
  
  // Text colors
  root.style.setProperty('--color-text-primary', themeColors.text.primary)
  root.style.setProperty('--color-text-secondary', themeColors.text.secondary)
  root.style.setProperty('--color-text-light', themeColors.text.light)
  root.style.setProperty('--color-text-inverse', themeColors.text.inverse)
  
  // Surface colors
  root.style.setProperty('--color-surface-light', themeColors.surface.light)
  root.style.setProperty('--color-surface-elevated', themeColors.surface.elevated)
  root.style.setProperty('--color-surface-dark', themeColors.surface.dark)
  
  // Border colors
  root.style.setProperty('--color-border-light', themeColors.border.light)
  root.style.setProperty('--color-border-main', themeColors.border.main)
  root.style.setProperty('--color-border-dark', themeColors.border.dark)
  
  // Status colors
  root.style.setProperty('--color-success-light', themeColors.success.light)
  root.style.setProperty('--color-success-main', themeColors.success.main)
  root.style.setProperty('--color-success-dark', themeColors.success.dark)
  
  root.style.setProperty('--color-error-light', themeColors.error.light)
  root.style.setProperty('--color-error-main', themeColors.error.main)
  root.style.setProperty('--color-error-dark', themeColors.error.dark)
  
  root.style.setProperty('--color-warning-light', themeColors.warning.light)
  root.style.setProperty('--color-warning-main', themeColors.warning.main)
  root.style.setProperty('--color-warning-dark', themeColors.warning.dark)
  
  root.style.setProperty('--color-info-light', themeColors.info.light)
  root.style.setProperty('--color-info-main', themeColors.info.main)
  root.style.setProperty('--color-info-dark', themeColors.info.dark)
}

/**
 * Initialize theme on app load
 */
export function initTheme() {
  const theme = getTheme()
  const accessibilityMode = getAccessibilityMode()
  applyTheme(theme, accessibilityMode)
}

